import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ScanLine } from 'lucide-react';
import type { Partner } from '@dru/types';
import { Input, cn } from '@dru/ui';
import { useBackLink } from '../lib/context';

/**
 * What the bench's one scan box was handed.
 *
 * Three different things carry a printed code, and only one of them is a
 * tube. The employee's ID card QR holds their employee code; an appointment
 * pass holds a long one-time token; a tube holds its specimen barcode. Read
 * as a search, an employee code would find nothing and look like a missing
 * tube — so it is named for what it is instead.
 */
export type Scanned =
  | { kind: 'specimen'; code: string }
  | { kind: 'subject'; code: string }
  | { kind: 'employee'; code: string }
  | { kind: 'pass' }
  | { kind: 'text'; code: string }
  | { kind: 'empty' };

const PASS_MIN = 20;

export function classifyScan(raw: string): Scanned {
  const v = raw.trim();
  if (!v) return { kind: 'empty' };
  // Specimen barcodes are issued as SPL-XXXXX-XXXXX.
  if (/^SPL-[A-Z0-9-]+$/i.test(v)) return { kind: 'specimen', code: v.toUpperCase() };
  // The bench's pseudonym: "Subject 63B0CE64", or the eight characters alone.
  const subj = v.replace(/^subject\s+/i, '');
  if (/^[0-9A-F]{8}$/i.test(subj)) return { kind: 'subject', code: subj.toUpperCase() };
  // An appointment pass is one long token with no spaces.
  if (v.length >= PASS_MIN && !/\s/.test(v)) return { kind: 'pass' };
  // An employee code from the ID card: letters, a dash, digits (DEMO-0004).
  if (/^[A-Z]{2,6}-\d{2,}$/i.test(v)) return { kind: 'employee', code: v.toUpperCase() };
  return { kind: 'text', code: v };
}

/** Plain words for what was scanned when it is not something this screen finds. */
export function scanRefusal(s: Scanned): string | null {
  if (s.kind === 'employee') return `${s.code} is an employee ID card, not a tube. Scan the barcode on the tube.`;
  if (s.kind === 'pass') return 'That is an appointment pass, not a tube. The front desk checks it in; scan the barcode on the tube here.';
  return null;
}

/**
 * The bench's scan box: a scanner types and presses Enter; a person can type
 * and wait. Either way the input is classified before it is searched.
 */
export function BenchScanBox({ value, onChange, onSubmit, label, placeholder, className }: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  label: string;
  placeholder: string;
  className?: string;
}) {
  return (
    <div className={cn('relative', className)}>
      <ScanLine aria-hidden className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onSubmit(); } }}
        placeholder={placeholder}
        aria-label={label}
        autoComplete="off"
        spellCheck={false}
        className="h-10 pl-9 font-mono placeholder:font-sans"
      />
    </div>
  );
}

/**
 * The selected venue, kept in the address as `?venue=`, so moving between
 * the lab screens — or refreshing — does not drop it. Only for accounts that
 * choose a venue; a venue's own login has one and ignores this.
 */
export function useVenueParam(v: { partner: Partner | null; setPartner: (p: Partner | null) => void; fixed: boolean }) {
  // useVenueFilter itself keeps `?venue=` and the picker in step for every
  // lab screen; this only reads the id to hand on in links.
  const [params] = useSearchParams();
  return v.fixed ? '' : (v.partner?.id ?? params.get('venue') ?? '');
}

/**
 * Where the reader was on a list, so coming back from a detail view or
 * another screen puts them on the same row. Keyed by the list's address,
 * which already carries its filters and page.
 */
export function useScrollMemory(ready: boolean) {
  const { pathname, search } = useLocation();
  // Decoded, so "a,b" and "a%2Cb" are the same list, and without the
  // opened tube, so the list and its detail view share one position.
  const key = `dru.scroll.${pathname}${(() => { try { return decodeURIComponent(search); } catch { return search; } })().replace(/[?&]sample=[^&]*/, '')}`;
  const restored = useRef('');
  useEffect(() => {
    if (!ready || restored.current === key) return;
    restored.current = key;
    try {
      const y = Number(sessionStorage.getItem(key) ?? '');
      if (y > 0) requestAnimationFrame(() => window.scrollTo({ top: y }));
    } catch { /* storage unavailable: start at the top */ }
  }, [ready, key]);
  const remember = () => { try { sessionStorage.setItem(key, String(window.scrollY)); } catch { /* best effort */ } };
  /** Back to the remembered row, after the list has rendered again. */
  const restore = () => {
    try {
      const y = Number(sessionStorage.getItem(key) ?? '');
      if (y > 0) setTimeout(() => window.scrollTo({ top: y }), 50);
    } catch { /* storage unavailable */ }
  };
  return { remember, restore };
}

/** "Back to Samples" and the like, from `?back=`. */
export function BenchBackLink({ className }: { className?: string }) {
  const back = useBackLink();
  if (!back) return null;
  return (
    <Link to={back.to}
          className={cn('inline-flex min-h-10 items-center gap-1.5 text-small font-medium text-primary-700 hover:text-primary-800', className)}>
      <ArrowLeft aria-hidden className="h-4 w-4" /> {back.label}
    </Link>
  );
}

/**
 * Why a tube's next act is not this login's, in the bench's words: what is
 * incomplete and which ROLE acts. Never a named person (there is no
 * assignment to name) and never a claim that anybody was notified.
 */
export function benchWaitingOn(status: string, permissions: ReadonlySet<string>): { reason: string; role: string | null } | null {
  const can = (p: string) => permissions.has(p);
  switch (status) {
    case 'ORDERED':
      return { reason: 'Not drawn yet.', role: 'Phlebotomy' };
    case 'COLLECTED':
    case 'IN_TRANSIT':
      return can('lab.sample-accession') ? null
        : { reason: 'Receive this specimen before entering results.', role: 'Laboratory technician' };
    case 'RECEIVED':
    case 'PROCESSING':
      return can('lab.result-enter') ? null : { reason: 'Results are entered at the bench.', role: 'Laboratory technician' };
    case 'RESULTED':
      return can('lab.result-validate') ? null
        : { reason: 'Entered values are checked by a reviewer other than the person who entered them.', role: 'Pathologist' };
    case 'VALIDATED':
      return can('lab.result-release') ? null : { reason: 'Validated values are released before anyone outside the lab sees them.', role: 'Pathologist' };
    case 'REJECTED':
    case 'RECOLLECT_REQUESTED':
      return { reason: 'Rejected — a new draw is requested.', role: 'Phlebotomy' };
    case 'RELEASED':
      return { reason: 'Released. Nothing left for the bench.', role: null };
    default:
      return null;
  }
}

/** A small "waiting on" line with the role, used on rows and in the panel. */
export function WaitingOnLine({ status, permissions, className }: { status: string; permissions: ReadonlySet<string>; className?: string }) {
  const w = benchWaitingOn(status, permissions);
  if (!w) return null;
  return (
    <p className={cn('text-caption text-neutral-600', className)}>
      {w.reason}
      {w.role && <> <span className="whitespace-nowrap font-medium text-neutral-800">Next: {w.role}</span></>}
    </p>
  );
}

/** Whether this login may open the check-in desk, and where it is. */
export function useCheckInHref(permissions: ReadonlySet<string>): string | null {
  const { pathname } = useLocation();
  if (!permissions.has('appointment.checkin')) return null;
  return pathname.startsWith('/partner') ? '/partner/check-in' : '/hospital/check-in';
}

/** A count of typed-but-unsaved values, so leaving can ask first. */
export function useUnsavedGuard(dirty: number) {
  const [, force] = useState(0);
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    // In-app links: the router has no blocker under BrowserRouter, so a
    // capture-phase listener asks before any same-site link is followed.
    const click = (e: MouseEvent) => {
      const a = (e.target as HTMLElement | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a || a.target === '_blank' || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey) return;
      const url = new URL(a.href, location.href);
      if (url.origin !== location.origin || (url.pathname === location.pathname && url.search === location.search)) return;
      if (!window.confirm(`You have ${dirty} typed value${dirty === 1 ? '' : 's'} not saved. Leave and discard ${dirty === 1 ? 'it' : 'them'}?`)) {
        e.preventDefault(); e.stopPropagation();
      }
    };
    window.addEventListener('beforeunload', warn);
    document.addEventListener('click', click, true);
    return () => { window.removeEventListener('beforeunload', warn); document.removeEventListener('click', click, true); };
  }, [dirty]);
  /** Ask before an in-page move (next subject, another tab) drops typed values. */
  return (fn: () => void) => () => {
    if (dirty && !window.confirm(`You have ${dirty} typed value${dirty === 1 ? '' : 's'} not saved. Discard ${dirty === 1 ? 'it' : 'them'}?`)) { force((n) => n + 1); return; }
    fn();
  };
}
